import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { Typography, Divider } from "@material-ui/core";
import {
	Page,
	PageContent,
	ScrollToTopOnMount,
	AddButton,
} from "frame/components";
import { sortByPropertyDate, useRouter } from "utils";
import {
	AddHazardToSiteForm,
	EditHazardToSiteForm,
	EditHazardForm,
} from "features/health-safety-forms";
import SiteHazardCard from "../cards/hazard-site-card";
import { isNotNullOrEmpty, isDefaultGuid } from "utils";
import { useSelector } from "react-redux";

const useStyles = makeStyles((theme) => ({
	root: {
		backgroundColor: theme.palette.white,
		position: "relative",
	},
	body: {
		marginTop: theme.spacing(3),
	},
	heading: {
		marginTop: theme.spacing(4),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(0),
		},
	},
	addButton: {
		position: "absolute",
		top: 15,
		right: 0,
		[theme.breakpoints.down("xs")]: {
			position: "relative",
			marginTop: theme.spacing(1),
			marginBottom: theme.spacing(3),
			top: 0,
		},
	},
	empty: {
		marginTop: theme.spacing(3),
	},
	divider: {
		marginTop: theme.spacing(2),
		height: 1,
	},
	header: {
		marginTop: theme.spacing(1),
	},
	title: {
		fontWeight: 600,
	},
	embeddedRoot: {
		maxHeight: "min(70vh, 720px)",
		overflowY: "auto",
	},
	embeddedContent: {
		paddingTop: theme.spacing(1),
	},
}));

const HazardSiteList = ({
	values,
	role,
	onAdd,
	onEdit,
	onDelete,
	variant = "page",
	siteId: siteIdProp,
}) => {
	const classes = useStyles();

	const { match } = useRouter();
	const siteId = siteIdProp || match?.params?.siteId;
	const isEmbedded = variant === "embedded";

	const [editId, setEditId] = useState(null);
	const [siteHazards, setSiteHazards] = useState([]);
	const [currentSite, setCurrentSite] = useState(null);
	const [viewId, setViewId] = useState();

	const siteList = useSelector(({ sites }) => sites.pagination?.results);

	useEffect(() => {
		if (!siteId || isDefaultGuid(siteId)) {
			return;
		}
		const fromValues = values?.id === siteId ? values : null;
		const fromList =
			siteList && siteList.filter((s) => s.id === siteId)[0];
		const site = fromValues || fromList;
		setSiteHazards(site ? site.hazards : []);
		setCurrentSite(site || null);
	}, [siteId, values, values?.hazards, siteList]);

	const isArchived = (item) => {
		if (item.status == null) {
			item.status = "Active";
		}
		return String(item.status).toLowerCase() === "archived";
	};

	const archivedSiteHazards = [...siteHazards]
		.filter((node) => isArchived(node))
		.sort(sortByPropertyDate("createdAt"));

	const activeSiteHazards = [...siteHazards]
		.filter((node) => !isArchived(node))
		.sort(sortByPropertyDate("createdAt"));

	const empty = siteHazards.length <= 0;
	const hasArchivedSiteHazards = archivedSiteHazards.length > 0;
	const hasActiveSiteHazards = activeSiteHazards.length > 0;

	const onEditHandler = (hazardId) => () => {
		onEdit();
		setEditId(hazardId);
	};

	const preExistingSiteHazards = isNotNullOrEmpty(siteHazards)
		? siteHazards.map((s) => s.hazardId)
		: [];

	const onView = (hazardId) => () => setViewId(hazardId);

	const onCancelView = () => setViewId(null);

	const renderSiteHazards = (list) =>
		list &&
		list.map((data, index) => (
			<SiteHazardCard
				key={index}
				currentSite={currentSite}
				onView={onView(data.hazardId)}
				onEdit={onEditHandler(data.hazardId)}
				onDelete={onDelete}
				hazard={data}
				role={role}
			/>
		));

	const forms = (
		<>
			{!isEmbedded && (
				<AddHazardToSiteForm
					siteId={siteId}
					preExistingSiteHazards={preExistingSiteHazards}
				/>
			)}
			{editId && (
				<EditHazardToSiteForm siteId={siteId} hazardId={editId} />
			)}
			{viewId && (
				<EditHazardForm readOnly id={viewId} onClose={onCancelView} />
			)}
		</>
	);

	const body = (
		<PageContent
			pad
			className={isEmbedded ? classes.embeddedContent : undefined}
		>
			{!isEmbedded && (
				<Typography variant="h2" className={classes.heading}>
					Hazards
				</Typography>
			)}

			{!isEmbedded && (
				<div className={classes.addButton}>
					<AddButton addTabTitle="Add hazard to site" onAdd={onAdd} />
				</div>
			)}

			{empty && hasActiveSiteHazards && (
				<Typography className={classes.empty} variant="body1">
					There are currently no Hazards listed.
				</Typography>
			)}

			<div className={classes.body}>
				{!empty && hasActiveSiteHazards && (
					<>
						<div className={classes.header}>
							<Typography className={classes.title} variant="h4">
								Active
							</Typography>
						</div>
						{renderSiteHazards(activeSiteHazards)}
					</>
				)}

				{!empty && hasArchivedSiteHazards && (
					<>
						<div className={classes.header}>
							<Typography className={classes.title} variant="h4">
								Archived
							</Typography>
							<Divider className={classes.divider} />
						</div>
						{renderSiteHazards(archivedSiteHazards)}
					</>
				)}
			</div>
		</PageContent>
	);

	if (isEmbedded) {
		return (
			<div className={classes.embeddedRoot}>
				{body}
				{forms}
			</div>
		);
	}

	return (
		<Page className={classes.root} title="Hazards">
			<ScrollToTopOnMount />
			{body}
			{forms}
		</Page>
	);
};

HazardSiteList.propTypes = {
	values: PropTypes.object.isRequired,
	role: PropTypes.string.isRequired,
	onAdd: PropTypes.func.isRequired,
	onEdit: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	variant: PropTypes.oneOf(["page", "embedded"]),
	siteId: PropTypes.string,
};

export default HazardSiteList;
